import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MultiSelectItem } from '@app/Models/multi-select-item';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ListItem } from '../../../../Models/list-item';
import { ModuleService } from '../../../../Services/module.service';
import { submissionNewNodeLevelToListAlias, SubmissionTypeConfigConstants } from '../submission-type-config.constants';
import { SubmissionTypeConfigService } from '../submission-type-config.service';
import { MatToolbar } from '@angular/material/toolbar';
import { MultiSelectComponent } from '../../../../../../../Common/multi-select/multi-select.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'aw-add-submission-type',
    templateUrl: './add-submission-type.component.html',
    styleUrls: ['./add-submission-type.component.scss'],
    imports: [MatToolbar, MultiSelectComponent, MatButton, MatIcon]
})
export class AddSubmissionTypeComponent implements OnInit, OnDestroy {
  @Output() formClosed = new EventEmitter<boolean>();
  submissionTypeList: ListItem[] = [];
  submissionTypeMultiSelectList: MultiSelectItem[] = [];
  selectedSubmissionTypeValues: string[] = [];
  errorText: string;
  listAlias: string;
  nodesInUse: string[];

  sidebarTitle: string = SubmissionTypeConfigConstants.defaultSidebarTitle;
  submissionTypeLabel: string =
    SubmissionTypeConfigConstants.defaultSubmissionTypeLabel;

  private isDestroyed$ = new Subject<void>();

  public constructor(
    private moduleService: ModuleService,
    private submissionTypeConfigService: SubmissionTypeConfigService
  ) {}

  ngOnInit(): void {
    this.loadSubmissionTypeList();

    this.submissionTypeConfigService.newNodes.pipe(takeUntil(this.isDestroyed$)).subscribe((_) => {
      this.closeForm(true);
    });
    this.submissionTypeConfigService.nodeSelected.pipe(takeUntil(this.isDestroyed$)).subscribe(() => {
      this.loadSubmissionTypeList();
    });
    this.submissionTypeConfigService.nodesInUse.pipe(takeUntil(this.isDestroyed$)).subscribe((nodes) => {
      this.nodesInUse = nodes;
    });

    this.moduleService
      .getFieldsUpdated()
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(() => this.loadSubmissionTypeList());
  }

  addNewNode(): void {
    const selectedItems = this.submissionTypeList.filter(
      (item) => this.selectedSubmissionTypeValues.indexOf(item.Value) !== -1
    );

    this.submissionTypeConfigService.addNewNodes(selectedItems);
    this.selectedSubmissionTypeValues = [];
  }

  closeForm(hasSaved?: boolean): void {
    this.formClosed.emit(hasSaved);
  }

  enableManageListItems(): boolean {
    if (this.submissionTypeList) {
      return false;
    }
    return true;
  }

  manageListItems(): void {
    this.moduleService.editList(this.listAlias, this.listAlias);
  }

  isSaveButtonDisabled(): boolean {
    return !this.selectedSubmissionTypeValues || this.selectedSubmissionTypeValues.length === 0;
  }

  onSubmissionTypeValuesChange(newValues: string[]): void {
    this.selectedSubmissionTypeValues = newValues;
  }

  isSubmissionTypeListEmpty(): boolean {
    return this.submissionTypeList.length === 0;
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  private loadSubmissionTypeList(): void {
    let listLevel = 0;
    if (!this.submissionTypeConfigService.selectedNode) {
      return;
    }
    listLevel = this.submissionTypeConfigService.selectedNode.level;

    if (!submissionNewNodeLevelToListAlias.get(listLevel)) {
      return;
    }

    this.listAlias = submissionNewNodeLevelToListAlias.get(listLevel).listAlias;
    this.updateLabels(listLevel);

    if (listLevel >= 3) {
      return;
    }

    this.submissionTypeConfigService
      .getListItems(this.listAlias)
      .then((result) => {
        if (this.nodesInUse) {
          this.submissionTypeList = result.filter(
            (type) => -1 === this.nodesInUse.indexOf(type.Value)
          );
        } else {
          this.submissionTypeList = result;
        }

        this.submissionTypeMultiSelectList = this.submissionTypeList.map(
          (submissionType) =>
            new MultiSelectItem(submissionType.Value, submissionType.Text)
        );
      });
  }

  private updateLabels(listLevel: number): void {
    const label = submissionNewNodeLevelToListAlias.get(listLevel).label;
    this.sidebarTitle = 'Add ' + label;
    this.submissionTypeLabel = label;
  }
}
